import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../store';
import { UserRole } from '../../types/Forms';
import { SingleLeadData } from '../../types/Leads';
import { DeleteExamPayload, DeleteExamResponse } from '../../types/Exams';
import { GetProfileResponse } from '../../types/Users';
import { EngagementsDetails } from '../../types/Engagements';
import {
  CrmControlAllItemsApiResponse,
  CrmControlItem,
  FindAllOverdueLEResponse,
} from '../../types/Control';
import { updateTranscriptionText } from '../../services/api';

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BACKEND_API,
  prepareHeaders: (headers, api) => {
    const token = (api.getState() as RootState).auth.token;
    const refreshToken = (api.getState() as RootState).auth.refreshToken;
    const lang = localStorage.getItem('i18nextLng') || 'en';
    // Detect refresh request by URL
    // extraOptions may not have url, so check headers for a custom flag or use a workaround
    // Instead, check if the request body is exactly { refreshToken } and method is POST
    // Or, set a custom header in baseQueryWithRefresh for refresh requests
    const extraOptions = api.extraOptions as any;
    if (extraOptions && extraOptions.isRefresh && refreshToken) {
      headers.set('Authorization', `Bearer ${refreshToken}`);
    } else if (token) {
      headers.set('Authorization', `Bearer ${token}`);
      headers.set('Accept-Language', lang);
    }
    return headers;
  },
});

const baseQueryWithRefresh: typeof baseQuery = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    // Try to refresh token
    const refreshToken = (api.getState() as RootState).auth.refreshToken;
    if (refreshToken) {
      // Set a custom flag to indicate this is a refresh request
      const refreshResult = await baseQuery(
        {
          url: '/auth/refresh',
          method: 'GET',
        },
        api,
        { ...extraOptions, isRefresh: true },
      );
      if (refreshResult.data) {
        // Save new token to state
        api.dispatch({ type: 'auth/setToken', payload: refreshResult.data });
        // Retry original request
        result = await baseQuery(args, api, extraOptions);
      } else {
        // Refresh failed, log out
        api.dispatch({ type: 'auth/logout' });
      }
    } else {
      api.dispatch({ type: 'auth/logout' });
    }
  }
  return result;
};

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: baseQueryWithRefresh,
  tagTypes: [
    'Course',
    'Lesson',
    'Users',
    'Exam',
    'AssistantExam',
    'Scoring',
    'Leads',
    'Engagements',
    'ScoreBoard',
    'Leaders',
    'CrmStatus',
    'StatsStatus',
  ],
  endpoints: (build) => ({
    getAllUsers: build.query({
      query: () => '/users', // <- specify the endpoint path
      providesTags: ['Users'],
    }),
    createUser: build.mutation({
      query: (body) => ({
        url: '/users',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Users'],
    }),
    editUser: build.mutation({
      query: (body) => ({
        url: '/users',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Users'],
    }),
    deleteUser: build.mutation({
      query: (body) => ({
        url: '/users',
        method: 'DELETE',
        body,
      }),
      invalidatesTags: ['Users'],
    }),
    getUserProfileData: build.query({
      query: () => ({
        url: '/users/profile',
        method: 'GET',
        transformResponse: (resp: GetProfileResponse) => ({
          ...resp.user,
          dob: resp.user.dob ? new Date(resp.user.dob) : null,
        }),
      }),
      providesTags: ['Users'],
    }),
    updateUserProfileData: build.mutation({
      query: (body) => ({
        url: '/users/profile',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Users'],
    }),
    getAllCourses: build.query({
      query: ({ role, studentId }) => {
        if (!role && !studentId) {
          return '/courses/all';
        }
        return `/study/courses/${studentId}`;
      },
      providesTags: ['Course'],
    }),
    createCourse: build.mutation({
      query: (formData: FormData) => ({
        url: '/courses',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Course'],
    }),
    updateCourse: build.mutation({
      query: (formData: FormData) => {
        return {
          url: '/courses',
          method: 'PATCH',
          body: formData,
        };
      },
      invalidatesTags: ['Course'],
    }),
    deleteCourse: build.mutation({
      query: (body) => ({
        url: '/courses',
        method: 'DELETE',
        body,
      }),
      invalidatesTags: ['Course'],
    }),
    getSingleCourse: build.query({
      query: (body) => {
        const { studentId, role, id, lang } = body;
        if (role !== UserRole.STUDENT) {
          return `/courses/${id}`;
        } else {
          return `/study/single/courses/${studentId}/${id}`;
        }
      },
      providesTags: (result, error, id) =>
        result
          ? [{ type: 'Course', id }] // Match `courseId` used in invalidatesTags
          : [],
    }),
    createLesson: build.mutation({
      query: (formData: FormData) => {
        return {
          url: `/courses/lesson`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Course', 'Lesson'],
    }),
    getSingleLesson: build.query({
      query: (body) => {
        const { studentId, role, lesson_id } = body;
        if (role !== UserRole.STUDENT) {
          return `/courses/lesson/${lesson_id}`;
        } else {
          return `/study/${studentId}/lesson/${lesson_id}`;
        }
      },
      providesTags: ['Lesson'],
    }),
    updateLesson: build.mutation({
      query: (body) => ({
        url: `/courses/lesson/${body.id}`,
        method: 'PATCH',
        body: body.formData as FormData,
      }),
      invalidatesTags: ['Course', 'Lesson'],
    }),
    deleteLesson: build.mutation({
      query: (body) => ({
        url: '/courses/lesson',
        method: 'DELETE',
        body,
      }),
      invalidatesTags: (result, error, { courseId }) => [
        { type: 'Course', id: courseId },
        'Lesson',
      ],
    }),

    deleteLessonVideo: build.mutation({
      query: (body) => ({
        url: '/courses/lesson-video',
        method: 'DELETE',
        body,
      }),
      invalidatesTags: ['Course', 'Lesson'],
    }),
    addLessonVideo: build.mutation({
      query: (body) => ({
        url: '/courses/lesson/add-video',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Course', 'Lesson'],
    }),
    migrateLesson: build.mutation({
      query: (body) => ({
        url: '/courses/lesson/migrate',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Course', 'Lesson', 'ScoreBoard', 'Exam', 'AssistantExam'],
    }),
    getStudentExams: build.query({
      query: (studentId) => `/study/exam/${studentId}`,
      providesTags: ['Exam'],
    }),
    getAllExams: build.query({
      query: (params = {}) => {
        const qs = new URLSearchParams();
        for (const [k, v] of Object.entries(params)) {
          if (v !== undefined && v !== null) qs.append(k, String(v));
        }
        return { url: `/courses/exam${qs.toString() ? `?${qs}` : ''}` };
      },
      providesTags: ['AssistantExam', 'Exam'],
    }),
    assistantGetExam: build.query({
      query: (args) => ({
        url: 'assistant/exam',
        params: args && Object.keys(args).length ? args : undefined,
      }),
      providesTags: ['AssistantExam'],
    }),
    assistantGetExamDetails: build.query({
      query: (id) => ({
        url: `assistant/exam/details/${id}`,
      }),
      providesTags: ['AssistantExam', 'Scoring'],
    }),
    assistantCreateExam: build.mutation({
      query: (body) => ({
        url: `/assistant/exam/create`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AssistantExam', 'Exam', 'Leads'],
    }),
    assistantDeleteExam: build.mutation<DeleteExamResponse, DeleteExamPayload>({
      query: (body) => ({
        url: `/assistant/exam/details/${body.examId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['AssistantExam', 'Exam', 'Leads'],
    }),
    assistantDeleteAssistantExam: build.mutation<DeleteExamResponse, DeleteExamPayload>({
      query: (body) => ({
        url: `/assistant/exam/delete-assistant/${body.examId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['AssistantExam', 'Exam', 'Leads'],
    }),
    getExamLeaders: build.query({
      query: () => ({
        url: `/score/leaders`,
        method: 'GET',
      }),
      providesTags: ['Scoring'],
    }),
    getAllUsersLeaders: build.query({
      query: (params) => ({
        url: `/score/all-leaders`,
        method: 'GET',
        params,
      }),
      providesTags: ['Scoring', 'Exam', 'AssistantExam'],
    }),
    getAllUsersLeadersOnStats: build.query({
      query: (params) => {
        const qp: any = params ? { ...params } : {};
        if (qp.statusFilter) {
          if (Array.isArray(qp.statusFilter)) {
            qp.statusFilter = qp.statusFilter.length ? qp.statusFilter.join(',') : undefined;
          } else if (typeof qp.statusFilter === 'string') {
            // already a string, leave as-is
          }
        }
        return {
          url: `/exam-stats`,
          method: 'GET',
          params: qp,
        };
      },
      providesTags: ['Scoring', 'Leaders'],
    }),
    leadsImport: build.mutation({
      query: (formData) => ({
        url: '/leads/import',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Leads'],
    }),
    leadsGetAllActive: build.query({
      query: (params) => ({
        url: '/leads/active',
        method: 'GET',
        params,
      }),
      providesTags: ['Leads'],
    }),
    leadsGetSingleLead: build.query<SingleLeadData, { id: string }>({
      query: ({ id }) => ({
        url: `/leads/details/${id}`,
        method: 'GET',
      }),
      providesTags: ['Leads'],
    }),
    leadsUpdate: build.mutation({
      query: (body) => {
        return {
          url: `/leads/update`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['Leads'],
    }),
    leadsEdit: build.mutation({
      query: (body) => ({
        url: `/leads/edit`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Leads'],
    }),
    leadsDeleteScreenshot: build.mutation({
      query: (body) => ({
        url: `/leads/delete-screenshot`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Leads'],
    }),
    leadsActionCreate: build.mutation({
      query: (body) => ({
        url: '/leads/new-action',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Leads'],
    }),
    leadStatusChange: build.mutation({
      query: (body) => ({
        url: '/leads/status',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Leads'],
    }),
    leadStageChange: build.mutation({
      query: (body) => ({
        url: '/leads/stage',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Leads'],
    }),
    leadsStepUpdate: build.mutation({
      query: (body) => ({
        url: `/leads/step-update`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Leads'],
    }),
    leadToUserConvert: build.mutation({
      query: (body) => ({
        url: '/leads/convert',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Leads', 'Users'],
    }),
    leadGetExaminations: build.query({
      query: (params) => ({
        url: `/leads/examinations`,
        method: 'GET',
        params,
      }),
      providesTags: ['Leads'],
    }),
    assistantCommentExam: build.mutation({
      query: (body) => ({
        url: 'assistant/exam/comment',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Leads', 'Exam', 'AssistantExam', 'Leaders'],
    }),

    recountTranscripts: build.mutation({
      query: () => ({
        url: 'assistant/recount-transcripts',
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['Leads', 'Exam', 'AssistantExam'],
    }),

    getAllEngagements: build.query({
      query: (params) => ({
        url: 'engagements',
        method: 'GET',
        params,
      }),
      providesTags: ['Engagements'],
    }),
    getEngagmentsUser: build.query<EngagementsDetails, string>({
      query: (id) => ({
        url: `engagements/user/${id}`,
        method: 'GET',
      }),
      providesTags: ['Engagements'],
    }),
    changeEngagementStatus: build.mutation({
      query: (body) => ({
        url: 'engagements/user-status',
        method: 'POST',
        body,
      }),

      invalidatesTags: ['Engagements'],
    }),
    updateEngagementDetails: build.mutation({
      query: (body) => ({
        url: 'engagements/flowdata-update',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Engagements'],
    }),
    addEngagementAction: build.mutation({
      query: (body) => ({
        url: 'engagements/add-action',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Engagements'],
    }),
    findAllScoreBoard: build.query({
      query: (params) => ({
        url: 'scoring-board',
        method: 'GET',
        params,
      }),
      providesTags: ['ScoreBoard'],
    }),
    addCommentsStats: build.mutation({
      query: (body) => ({
        url: 'exam-stats/add-comments',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Scoring', 'Leaders', 'StatsStatus'],
    }),
    getAllStatsStatuses: build.query({
      query: () => ({
        url: 'exam-stats/statuses',
        method: 'GET',
      }),
      providesTags: ['Scoring', 'Leaders', 'StatsStatus'],
    }),
    addNewStatsStatus: build.mutation({
      query: (body) => ({
        url: 'exam-stats/add-status',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Scoring', 'Leaders', 'StatsStatus'],
    }),
    editStatsStatus: build.mutation({
      query: (body) => ({
        url: 'exam-stats/edit-status',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Scoring', 'Leaders', 'StatsStatus'],
    }),
    deleteStatsStatus: build.mutation({
      query: (body) => ({
        url: 'exam-stats/delete-status',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Scoring', 'Leaders', 'StatsStatus'],
    }),
    getLeadsEngagementsOverdues: build.query<
      CrmControlAllItemsApiResponse,
      {
        page: number;
        perPage: number;
        sort: 'asc' | 'desc';
        sortKey: string;
        search: string;
        statusFilter: number;
        skip?: boolean;
      }
    >({
      query: (params) => ({
        url: 'crm-control/overdue-le',
        method: 'GET',
        params,
      }),
      providesTags: ['Leads', 'Engagements'],
    }),
    getUnprocessedTranscriptions: build.query({
      query: (params) => ({
        url: 'crm-control/unprocessed',
        method: 'GET',
        params,
      }),
      providesTags: ['Leads', 'Engagements', 'Exam'],
    }),
    updateCrmItem: build.mutation({
      query: (body) => ({
        url: 'crm-control/update-crm-item',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Leads', 'Engagements', 'Exam', 'CrmStatus'],
    }),
    getAllCrmControlStatuses: build.query({
      query: () => ({
        url: 'crm-control/statuses',
        method: 'GET',
      }),
      providesTags: ['Leads', 'Engagements', 'Exam', 'CrmStatus'],
    }),
    addNewCrmStatus: build.mutation({
      query: (body) => ({
        url: 'crm-control/add-status',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Leads', 'Engagements', 'Exam', 'CrmStatus'],
    }),
    editCrmStatus: build.mutation({
      query: (body) => ({
        url: 'crm-control/edit-status',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Leads', 'Engagements', 'Exam', 'CrmStatus'],
    }),
    deleteCrmStatus: build.mutation({
      query: (body) => ({
        url: 'crm-control/delete-status',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Leads', 'Engagements', 'Exam', 'CrmStatus'],
    }),
    getStudentScoreById: build.query({
      query: ({ studentId, courseId }) => ({
        url: `/study/get-student-score/${studentId}`,
        method: 'GET',
        params: { courseId },
      }),
      providesTags: ['Scoring', 'Exam', 'AssistantExam'],
    }),
    trainLesson: build.mutation({
      query: (body) => ({
        url: '/courses/lesson/train',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Course', 'Lesson', 'ScoreBoard', 'Exam', 'AssistantExam'],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetAllUsersQuery,
  useCreateUserMutation,
  useGetAllCoursesQuery,
  useGetSingleCourseQuery,
  useCreateLessonMutation,
  useUpdateLessonMutation,
  useDeleteLessonMutation,
  useDeleteLessonVideoMutation,
  useAddLessonVideoMutation,
  useGetSingleLessonQuery,
  useEditUserMutation,
  useDeleteUserMutation,
  useGetUserProfileDataQuery,
  useUpdateUserProfileDataMutation,
  useUpdateCourseMutation,
  useDeleteCourseMutation,
  useCreateCourseMutation,
  useGetStudentExamsQuery,
  useGetAllExamsQuery,
  useAssistantGetExamQuery,
  useAssistantGetExamDetailsQuery,
  useAssistantCreateExamMutation,
  useAssistantDeleteExamMutation,
  useGetExamLeadersQuery,
  useLeadsImportMutation,
  useLeadsGetAllActiveQuery,
  useLeadsGetSingleLeadQuery,
  useLeadsUpdateMutation,
  useLeadsStepUpdateMutation,
  useLeadsActionCreateMutation,
  useLeadStatusChangeMutation,
  useLeadStageChangeMutation,
  useLeadToUserConvertMutation,
  useLeadGetExaminationsQuery,
  useAssistantCommentExamMutation,
  useRecountTranscriptsMutation,
  useGetAllEngagementsQuery,
  useGetEngagmentsUserQuery,
  useChangeEngagementStatusMutation,
  useUpdateEngagementDetailsMutation,
  useAddEngagementActionMutation,
  useFindAllScoreBoardQuery,
  useGetLeadsEngagementsOverduesQuery,
  useGetAllUsersLeadersQuery,
  useAssistantDeleteAssistantExamMutation,
  useGetAllUsersLeadersOnStatsQuery,
  useAddCommentsStatsMutation,
  useGetAllStatsStatusesQuery,
  useAddNewStatsStatusMutation,
  useMigrateLessonMutation,
  useGetUnprocessedTranscriptionsQuery,
  useUpdateCrmItemMutation,
  useGetAllCrmControlStatusesQuery,
  useAddNewCrmStatusMutation,
  useEditCrmStatusMutation,
  useDeleteCrmStatusMutation,
  useEditStatsStatusMutation,
  useDeleteStatsStatusMutation,
  useGetStudentScoreByIdQuery,
  useTrainLessonMutation,
} = usersApi;
